import { z } from 'zod'

// Base API Response Schema
export const ApiResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  data: z.any().nullable()
})

// Paginated Response Schema
export const PaginatedResponseSchema = z.object({
  totalPage: z.number(),
  totalElements: z.number(),
  totalOfAllElements: z.number(),
  data: z.array(z.any())
})

// User Schema
export const UserSchema = z.object({
  userId: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  emailAddress: z.string().email(),
  gender: z.enum(['MALE', 'FEMALE']).nullable(),
  phoneNumber: z.string(),
  profilePicture: z.string().nullable(),
  profileThemeColorCode: z.string().nullable()
})

// Admin User Schema - comprehensive with all available fields
export const AdminUserSchema = z.object({
  id: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  email: z.string().email(),
  phoneNumber: z.string().optional(),
  profilePicture: z.string().nullable().optional(),
  socialLinks: z.array(z.string()).optional(),
  isDefaultPassword: z.boolean().optional(),
  userRole: z.string().optional(),
  otpChannel: z.string().optional(),
  lastLoginWith2FA: z.string().optional(),
  hasActivatedAccount: z.boolean().optional(),
  isTwoFactorEnabled: z.boolean().optional(),
  // Computed field for display
  fullName: z.string()
})

// Authentication Schemas
export const LoginRequestSchema = z.object({
  emailAddress: z.string().email(),
  password: z.string().min(6)
})

// User Profile Response (common structure)
export const UserProfileResponseSchema = z.object({
  userId: z.string(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  emailAddress: z.string().email().optional(),
  phoneNumber: z.string().optional(),
  profilePicture: z.string().nullable().optional(),
  socialLinks: z.array(z.string()).optional(),
  isDefaultPassword: z.boolean().optional(),
  userRole: z.string().optional(),
  otpChannel: z.string().optional(),
  lastLoginWith2FA: z.string().optional(),
  hasActivatedAccount: z.boolean().optional(),
  isTwoFactorEnabled: z.boolean().optional()
})

// Login Response Schema (handles both 2FA enabled and disabled cases)
export const LoginResponseSchema = z.object({
  token: z.string().optional(), // Only present when 2FA is enabled
  userProfileResponse: UserProfileResponseSchema
})

// OTP Verification Request Schema
export const VerifyOtpRequestSchema = z.object({
  userId: z.string(),
  otp: z.string().length(6)
})

// OTP Verification Response Schema (same as full login response)
export const VerifyOtpResponseSchema = z.object({
  token: z.string(),
  userProfileResponse: UserProfileResponseSchema
})

// Document Schema
export const DocumentSchema = z.object({
  documentId: z.string(),
  title: z.string(),
  content: z.string(),
  category: z.string(),
  status: z.enum(['DRAFT', 'PUBLISHED']),
  author: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  publishedAt: z.string().nullable(),
  tags: z.array(z.string()).optional(),
  featuredImage: z.string().nullable()
})

export const CreateDocumentSchema = z.object({
  title: z.string().min(1),
  content: z.string().min(1),
  category: z.string(),
  status: z.enum(['DRAFT', 'PUBLISHED']).default('DRAFT'),
  tags: z.array(z.string()).optional(),
  featuredImage: z.string().optional()
})

export const UpdateDocumentSchema = CreateDocumentSchema.partial().extend({
  documentId: z.string()
})

// Event Schema
export const EventSchema = z.object({
  eventId: z.string(),
  title: z.string(),
  description: z.string(),
  location: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  category: z.string(),
  status: z.enum(['UPCOMING', 'ONGOING', 'COMPLETED', 'CANCELLED']),
  registrationRequired: z.boolean(),
  maxAttendees: z.number().nullable(),
  currentAttendees: z.number().default(0),
  organizer: z.string(),
  featuredImage: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string()
})

export const CreateEventSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  location: z.string(),
  startDate: z.string(),
  endDate: z.string(),
  category: z.string(),
  registrationRequired: z.boolean().default(false),
  maxAttendees: z.number().nullable(),
  featuredImage: z.string().optional()
})

export const UpdateEventSchema = CreateEventSchema.partial().extend({
  eventId: z.string()
})

// Job Schema
export const JobSchema = z.object({
  jobId: z.string(),
  title: z.string(),
  description: z.string(),
  requirements: z.array(z.string()),
  location: z.string(),
  jobType: z.enum(['FULL_TIME', 'PART_TIME', 'CONTRACT', 'INTERNSHIP']),
  department: z.string(),
  salaryRange: z.string().nullable(),
  applicationDeadline: z.string(),
  status: z.enum(['OPEN', 'CLOSED', 'FILLED']),
  postedBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
})

export const CreateJobSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  requirements: z.array(z.string()),
  location: z.string(),
  jobType: z.enum(['FULL_TIME', 'PART_TIME', 'CONTRACT', 'INTERNSHIP']),
  department: z.string(),
  salaryRange: z.string().optional(),
  applicationDeadline: z.string()
})

// Tender Schema
export const TenderSchema = z.object({
  tenderId: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.string(),
  budget: z.number().nullable(),
  submissionDeadline: z.string(),
  status: z.enum(['OPEN', 'CLOSED', 'AWARDED']),
  requirements: z.array(z.string()),
  documents: z.array(z.string()),
  publishedBy: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
})

export const CreateTenderSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  category: z.string(),
  budget: z.number().optional(),
  submissionDeadline: z.string(),
  requirements: z.array(z.string()),
  documents: z.array(z.string()).optional()
})

// Press Release Schema (for list/filter responses)
export const PressReleaseSchema = z.object({
  pressReleaseId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByUsername: z.string(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.string(),
  landingPage: z.boolean(),
  pressRelease: z.boolean(),
  pressReleaseBanner: z.boolean(),
  allowComment: z.boolean(),
  content: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

// Press Release Detail Schema (for get by ID responses with audit fields)
export const PressReleaseDetailSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.string(),
  landingPage: z.boolean(),
  pressRelease: z.boolean(),
  pressReleaseBanner: z.boolean(),
  allowComment: z.boolean(),
  content: z.string(),
  status: z.string(),
  datePublished: z.string().nullable()
})

// Press Release Filter Parameters Schema
export const PressReleaseFilterParamsSchema = z.object({
  title:z.string().optional(),
  articleTag: z.string().optional(),
  landingPage: z.boolean().optional(),
  pressRelease: z.boolean().optional(),
  pressReleaseBanner: z.boolean().optional(),
  allowComment: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional()
})

// Press Release Stats Schema
export const PressReleaseStatsSchema = z.object({
  draftPressReleases: z.number()
})

// Create Press Release Schema
export const CreatePressReleaseSchema = z.object({
  title: z.string().min(1),
  coverImageTitle: z.string(),
  articleTag: z.array(z.string()), // Array of tag strings
  landingPage: z.boolean(),
  pressRelease: z.boolean(),
  pressReleaseBanner: z.boolean(),
  allowComment: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']).default('DRAFT'),
  coverImageUrl: z.string() // Image URL string (required for create) - API expects this field name
})

// Update Press Release Schema
export const UpdatePressReleaseSchema = z.object({
  title: z.string().min(1),
  coverImageTitle: z.string(),
  articleTag: z.array(z.string()), // Array of tag strings
  landingPage: z.boolean(),
  pressRelease: z.boolean(),
  pressReleaseBanner: z.boolean(),
  allowComment: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  coverImageUrl: z.string().optional() // Image URL string (optional for update) - API expects this field name
})

// Press Release Form Schema (for react-hook-form validation)
export const PressReleaseFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  content: z.string().min(1, 'Content is required'),
  coverImage: z.string().default(''), // Image URL string
  coverImageTitle: z.string().default(''),
  articleTag: z.string().default(''), // Comma-separated string (will be parsed to array on submit)
  landingPage: z.boolean().default(false),
  pressRelease: z.boolean().default(true),
  pressReleaseBanner: z.boolean().default(false),
  allowComment: z.boolean().default(true),
})

// Website Page Schema
export const WebPageSchema = z.object({
  pageId: z.string(),
  title: z.string(),
  slug: z.string(),
  content: z.string(),
  metaTitle: z.string().nullable(),
  metaDescription: z.string().nullable(),
  status: z.enum(['DRAFT', 'PUBLISHED']),
  template: z.string(),
  createdAt: z.string(),
  updatedAt: z.string()
})

// Region and Country Schemas
export const RegionSchema = z.object({
  regionId: z.string(),
  name: z.string(),
  code: z.string(),
  description: z.string().nullable()
})

export const CountrySchema = z.object({
  countryId: z.string(),
  name: z.string(),
  code: z.string(),
  region: RegionSchema,
  flag: z.string().nullable(),
  description: z.string().nullable()
})

export const MemberCountrySchema = CountrySchema.extend({
  membershipDate: z.string(),
  status: z.enum(['ACTIVE', 'INACTIVE', 'SUSPENDED'])
})

// Account Deletion Request Schema
export const AccountDeletionRequestSchema = z.object({
  userId: z.string(),
  reason: z.enum(['PRIVACY_CONCERNS', 'NO_LONGER_NEEDED', 'OTHERS']),
  deleteAt: z.string(),
  actorType: z.enum(['RESIDENT', 'ADMIN']),
  createdAt: z.string(),
  description: z.string(),
  user: UserSchema
})

// Strategic Pillar Schema (for list/filter responses)
export const StrategicPillarSchema = z.object({
  strategicPillarId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByUsername: z.string(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.string(),
  landingPagePrioritiesOfAfsa: z.boolean(),
  whoWeAre: z.boolean(),
  countries: z.boolean(),
  history: z.boolean(),
  member: z.boolean(),
  content: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

// Strategic Pillar Detail Schema (for get by ID responses with audit fields)
export const StrategicPillarDetailSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.string(),
  landingPagePrioritiesOfAfsa: z.boolean(),
  whoWeAre: z.boolean(),
  countries: z.boolean(),
  history: z.boolean(),
  member: z.boolean(),
  content: z.string(),
  status: z.string(),
  datePublished: z.string().nullable()
})

// Strategic Pillar Filter Parameters Schema
export const StrategicPillarFilterParamsSchema = z.object({
  title: z.string().optional(),
  articleTag: z.string().optional(),
  landingPagePrioritiesOfAfsa: z.boolean().optional(),
  whoWeAre: z.boolean().optional(),
  countries: z.boolean().optional(),
  history: z.boolean().optional(),
  member: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional()
})

// Create Strategic Pillar Schema
export const CreateStrategicPillarSchema = z.object({
  title: z.string().min(1),
  coverImageTitle: z.string(),
  articleTag: z.array(z.string()),
  landingPagePrioritiesOfAfsa: z.boolean(),
  whoWeAre: z.boolean(),
  countries: z.boolean(),
  history: z.boolean(),
  member: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']).default('DRAFT'),
  coverImageUrl: z.string()
})

// Update Strategic Pillar Schema
export const UpdateStrategicPillarSchema = z.object({
  title: z.string().min(1),
  coverImageTitle: z.string(),
  articleTag: z.array(z.string()),
  landingPagePrioritiesOfAfsa: z.boolean(),
  whoWeAre: z.boolean(),
  countries: z.boolean(),
  history: z.boolean(),
  member: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  coverImageUrl: z.string().optional()
})

// Strategic Pillar Form Schema (for react-hook-form validation)
export const StrategicPillarFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  content: z.string().min(1, 'Content is required'),
  coverImage: z.string().default(''),
  coverImageTitle: z.string().default(''),
  articleTag: z.string().default(''),
  landingPagePrioritiesOfAfsa: z.boolean().default(false),
  whoWeAre: z.boolean().default(false),
  countries: z.boolean().default(false),
  history: z.boolean().default(false),
  member: z.boolean().default(false),
})

// Programme Schema (for list/filter responses)
export const ProgrammeSchema = z.object({
  programmeId: z.string(),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByUsername: z.string(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.string(),
  landingPageAfsaProgramme: z.boolean(),
  allowComments: z.boolean(),
  content: z.string(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

// Programme Detail Schema (for get by ID responses with audit fields)
export const ProgrammeDetailSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  title: z.string(),
  coverImage: z.string(),
  coverImageTitle: z.string(),
  articleTag: z.string(),
  landingPageAfsaProgramme: z.boolean(),
  allowComments: z.boolean(),
  content: z.string(),
  status: z.string(),
  datePublished: z.string().nullable()
})

// Programme Filter Parameters Schema
export const ProgrammeFilterParamsSchema = z.object({
  title: z.string().optional(),
  articleTag: z.string().optional(),
  landingPageAfsaProgramme: z.boolean().optional(),
  allowComments: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional()
})

// Create Programme Schema
export const CreateProgrammeSchema = z.object({
  title: z.string().min(1),
  coverImageTitle: z.string().optional(),
  articleTag: z.array(z.string()), // Array of strings
  landingPageAfsaProgramme: z.boolean(),
  allowComments: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']).default('DRAFT'),
  coverImageUrl: z.string() // API expects coverImageUrl
})

// Update Programme Schema
export const UpdateProgrammeSchema = z.object({
  title: z.string().min(1),
  coverImageTitle: z.string().optional(),
  articleTag: z.array(z.string()), // Array of strings
  landingPageAfsaProgramme: z.boolean(),
  allowComments: z.boolean(),
  content: z.string().min(1),
  status: z.enum(['DRAFT', 'PUBLISH']).optional(),
  coverImageUrl: z.string().optional() // API expects coverImageUrl
})

// Programme Form Schema (for react-hook-form validation)
export const ProgrammeFormSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  content: z.string().min(1, 'Content is required'),
  coverImage: z.string().default(''),
  coverImageTitle: z.string().default(''),
  articleTag: z.string().default(''),
  landingPageAfsaProgramme: z.boolean().default(false),
  allowComments: z.boolean().default(true),
})

// Publication Schema (for list/filter responses)
export const PublicationSchema = z.object({
  publicationId: z.string(),
  title: z.string(),
  description: z.string(),
  publicationUrl: z.string(),
  publicationCategory: z.string(),
  legalFoundation: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  createdAt: z.string(),
  datePublished: z.string().nullable(),
  createdBy: z.string(),
  createdByName: z.string(),
  totalViews: z.number().optional().default(0),
  totalDownloads: z.number().optional().default(0)
})

// Publication Filter Parameters Schema
export const PublicationFilterParamsSchema = z.object({
  publicationCategory: z.string().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional()
})

// Publication Stats Schema
export const PublicationStatsSchema = z.object({
  draftPublications: z.number(),
  publishedPublications: z.number(),
  totalPublications: z.number()
})

// Create Publication Schema
export const CreatePublicationSchema = z.object({
  fileUrl: z.string().url(),
  title: z.string().min(1),
  description: z.string().min(1),
  publicationCategory: z.string(),
  legalFoundation: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISH']).default('DRAFT')
})

// Update Publication Schema
export const UpdatePublicationSchema = z.object({
  fileUrl: z.string().url().optional(),
  title: z.string().min(1).optional(),
  description: z.string().min(1).optional(),
  publicationCategory: z.string().optional(),
  legalFoundation: z.boolean().optional(),
  status: z.enum(['DRAFT', 'PUBLISH']).optional()
})

// Query Parameters Schema
export const PaginationParamsSchema = z.object({
  page: z.number().min(1).default(1),
  size: z.number().min(1).max(100).default(10)
})

export const SearchParamsSchema = z.object({
  searchBy: z.string().optional(),
  category: z.string().optional(),
  status: z.string().optional()
})

// Export types
export type ApiResponse<T = unknown> = z.infer<typeof ApiResponseSchema> & { data: T }
export type PaginatedResponse<T = unknown> = z.infer<typeof PaginatedResponseSchema> & { data: T[] }

export type User = z.infer<typeof UserSchema>
export type AdminUser = z.infer<typeof AdminUserSchema>
export type LoginRequest = z.infer<typeof LoginRequestSchema>
export type LoginResponse = z.infer<typeof LoginResponseSchema>
export type UserProfileResponse = z.infer<typeof UserProfileResponseSchema>
export type VerifyOtpRequest = z.infer<typeof VerifyOtpRequestSchema>
export type VerifyOtpResponse = z.infer<typeof VerifyOtpResponseSchema>

export type Document = z.infer<typeof DocumentSchema>
export type CreateDocument = z.infer<typeof CreateDocumentSchema>
export type UpdateDocument = z.infer<typeof UpdateDocumentSchema>

export type Event = z.infer<typeof EventSchema>
export type CreateEvent = z.infer<typeof CreateEventSchema>
export type UpdateEvent = z.infer<typeof UpdateEventSchema>

export type Job = z.infer<typeof JobSchema>
export type CreateJob = z.infer<typeof CreateJobSchema>

export type Tender = z.infer<typeof TenderSchema>
export type CreateTender = z.infer<typeof CreateTenderSchema>

export type PressRelease = z.infer<typeof PressReleaseSchema>
export type PressReleaseDetail = z.infer<typeof PressReleaseDetailSchema>
export type PressReleaseFilterParams = z.infer<typeof PressReleaseFilterParamsSchema>
export type PressReleaseStats = z.infer<typeof PressReleaseStatsSchema>
export type CreatePressRelease = z.infer<typeof CreatePressReleaseSchema>
export type UpdatePressRelease = z.infer<typeof UpdatePressReleaseSchema>
export type PressReleaseFormData = z.infer<typeof PressReleaseFormSchema>

export type StrategicPillar = z.infer<typeof StrategicPillarSchema>
export type StrategicPillarDetail = z.infer<typeof StrategicPillarDetailSchema>
export type StrategicPillarFilterParams = z.infer<typeof StrategicPillarFilterParamsSchema>
export type CreateStrategicPillar = z.infer<typeof CreateStrategicPillarSchema>
export type UpdateStrategicPillar = z.infer<typeof UpdateStrategicPillarSchema>
export type StrategicPillarFormData = z.infer<typeof StrategicPillarFormSchema>

export type Programme = z.infer<typeof ProgrammeSchema>
export type ProgrammeDetail = z.infer<typeof ProgrammeDetailSchema>
export type ProgrammeFilterParams = z.infer<typeof ProgrammeFilterParamsSchema>
export type CreateProgramme = z.infer<typeof CreateProgrammeSchema>
export type UpdateProgramme = z.infer<typeof UpdateProgrammeSchema>
export type ProgrammeFormData = z.infer<typeof ProgrammeFormSchema>

export const PublicationCategorySchema = z.object({
  name: z.string(),
  value: z.string()
})

export type Publication = z.infer<typeof PublicationSchema>
export type PublicationCategory = z.infer<typeof PublicationCategorySchema>
export type PublicationFilterParams = z.infer<typeof PublicationFilterParamsSchema>
export type PublicationStats = z.infer<typeof PublicationStatsSchema>
export type CreatePublication = z.infer<typeof CreatePublicationSchema>
export type UpdatePublication = z.infer<typeof UpdatePublicationSchema>

// Folder Schemas
export const FolderSchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  folderName: z.string()
})

export const FolderListItemSchema = z.object({
  folderId: z.string(),
  folderName: z.string()
})

export const CreateFolderSchema = z.object({
  folderName: z.string().min(1)
})

export const UpdateFolderSchema = z.object({
  folderName: z.string().min(1)
})

export const FolderSearchParamsSchema = z.object({
  searchBy: z.string().optional(),
  size: z.number().min(1).default(10),
  page: z.number().min(1).default(1)
})

export type Folder = z.infer<typeof FolderSchema>
export type FolderListItem = z.infer<typeof FolderListItemSchema>
export type CreateFolder = z.infer<typeof CreateFolderSchema>
export type UpdateFolder = z.infer<typeof UpdateFolderSchema>
export type FolderSearchParams = z.infer<typeof FolderSearchParamsSchema>

// Gallery Schemas
export const GallerySchema = z.object({
  id: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
  createdBy: z.string(),
  updatedBy: z.string().nullable(),
  deletedBy: z.string().nullable(),
  folderId: z.string(),
  title: z.string(),
  photoUrls: z.array(z.string()),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISH']),
  datePublished: z.string()
})

export const GalleryTitleResponseSchema = z.object({
  title: z.string(),
  photoUrls: z.array(z.string()),
  dateCreated: z.string(),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.string(),
  datePublished: z.string()
})

export const GalleryFolderResponseSchema = z.object({
  folderId: z.string(),
  folderName: z.string(),
  titleResponseList: z.array(GalleryTitleResponseSchema)
})

export const GalleryDetailSchema = z.object({
  galleryId: z.string(),
  folderId: z.string(),
  folderName: z.string(),
  title: z.string(),
  photoUrls: z.array(z.string()),
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.string(),
  datePublished: z.string(),
  createdAt: z.string()
})

export const CreateGallerySchema = z.object({
  folderId: z.string(),
  title: z.string().min(1),
  photoUrls: z.array(z.string().url()), // Array of photo URLs from image uploader
  isKeyHighlight: z.boolean(),
  isMultimedia: z.boolean(),
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const UpdateGallerySchema = z.object({
  folderId: z.string().optional(),
  title: z.string().min(1).optional(),
  isKeyHighlight: z.boolean().optional(),
  isMultimedia: z.boolean().optional()
})

export const UpdateGalleryStatusSchema = z.object({
  status: z.enum(['DRAFT', 'PUBLISH'])
})

export const GalleryStatsSchema = z.object({
  total: z.number(),
  published: z.number(),
  draft: z.number()
})

export type Gallery = z.infer<typeof GallerySchema>
export type GalleryTitleResponse = z.infer<typeof GalleryTitleResponseSchema>
export type GalleryFolderResponse = z.infer<typeof GalleryFolderResponseSchema>
export type GalleryDetail = z.infer<typeof GalleryDetailSchema>
export type CreateGallery = z.infer<typeof CreateGallerySchema>
export type UpdateGallery = z.infer<typeof UpdateGallerySchema>
export type UpdateGalleryStatus = z.infer<typeof UpdateGalleryStatusSchema>
export type GalleryStats = z.infer<typeof GalleryStatsSchema>

export type WebPage = z.infer<typeof WebPageSchema>
export type Region = z.infer<typeof RegionSchema>
export type Country = z.infer<typeof CountrySchema>
export type MemberCountry = z.infer<typeof MemberCountrySchema>
export type AccountDeletionRequest = z.infer<typeof AccountDeletionRequestSchema>

export type PaginationParams = z.infer<typeof PaginationParamsSchema>
export type SearchParams = z.infer<typeof SearchParamsSchema>